import { Fragment } from "react"
import styled from "styled-components"
import type { TestDriveStepStyle } from "../types"

export interface StepInfo {
  /** Rótulo curto maiúsculo (Trilha): "MODELO". */
  label: string
  /** Nome legível (Numerado/Progresso): "Modelo". */
  name: string
}

interface StepperProps {
  steps: StepInfo[]
  currentIndex: number
  variant: TestDriveStepStyle
}

type State = "current" | "completed" | "pending"
const stateOf = (i: number, current: number): State =>
  i < current ? "completed" : i === current ? "current" : "pending"

/** Indicador de passos com 3 estilos (aba Layout → Estilo dos passos). Usa as CSS
    vars `--td-step-current/completed/pending` (aba Design). */
export const Stepper = ({ steps, currentIndex, variant }: StepperProps) => {
  const total = steps.length

  if (variant === "numerado") {
    return (
      <Numbered>
        {steps.map((s, i) => (
          <Fragment key={s.label}>
            {i > 0 && <NumConnector $filled={i <= currentIndex} />}
            <NumItem $state={stateOf(i, currentIndex)}>
              <NumCircle $state={stateOf(i, currentIndex)}>{i + 1}</NumCircle>
              <NumName>{s.name}</NumName>
            </NumItem>
          </Fragment>
        ))}
      </Numbered>
    )
  }

  if (variant === "progresso") {
    return (
      <Progress>
        <ProgressLabel>
          Passo {currentIndex + 1} de {total}
          <strong> · {steps[currentIndex]?.name}</strong>
        </ProgressLabel>
        <ProgressTrack>
          <ProgressFill style={{ width: `${((currentIndex + 1) / total) * 100}%` }} />
        </ProgressTrack>
      </Progress>
    )
  }

  // trilha (default)
  return (
    <Trilha>
      <TrilhaLabels>
        <TrilhaMeta>{`${currentIndex + 1} de ${total}`}</TrilhaMeta>
        <TrilhaName>{steps[currentIndex]?.name}</TrilhaName>
      </TrilhaLabels>
      <TrilhaTrack>
        {steps.map((s, i) => (
          <TrilhaSeg key={s.label} $state={stateOf(i, currentIndex)} />
        ))}
      </TrilhaTrack>
    </Trilha>
  )
}

const segColor = (state: State): string =>
  state === "current"
    ? "var(--td-step-current, var(--buttons-background, var(--general-primary, #0027d4)))"
    : state === "completed"
      ? "var(--td-step-completed, var(--buttons-background, var(--general-primary, #0027d4)))"
      : "var(--td-step-pending, var(--general-border, #e2e8f0))"

// ——— Trilha ———
const Trilha = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`
const TrilhaLabels = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`
const TrilhaMeta = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--td-step-subtitle, var(--text-body, #64748b));
  white-space: nowrap;
`
const TrilhaTrack = styled.div`
  display: flex;
  gap: 4px;
`
const TrilhaSeg = styled.span<{ $state: State }>`
  flex: 1;
  height: 3px;
  border-radius: 3px;
  background: ${({ $state }) => segColor($state)};
`
const TrilhaName = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--td-step-title, var(--text-title, #0f172a));
  white-space: nowrap;
`

// ——— Numerado ———
const Numbered = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`
const NumConnector = styled.span<{ $filled: boolean }>`
  flex: 1;
  min-width: 0.75rem;
  height: 2px;
  border-radius: 2px;
  margin: 0 0.5rem;
  background: ${({ $filled }) =>
    $filled
      ? "var(--td-step-completed, var(--general-primary, #0027d4))"
      : "var(--td-step-pending, var(--general-border, #e2e8f0))"};
`
const NumItem = styled.span<{ $state: State }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`
const NumCircle = styled.span<{ $state: State }>`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ $state }) =>
    $state === "pending"
      ? "var(--td-step-subtitle, var(--text-body, #64748b))"
      : "var(--buttons-text, #fff)"};
  background: ${({ $state }) => segColor($state)};
`
const NumName = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  color: var(--td-step-title, var(--text-title, #0f172a));

  @media (max-width: 767px) {
    display: none;
  }
`

// ——— Progresso ———
const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`
const ProgressLabel = styled.span`
  font-size: 0.8125rem;
  color: var(--td-step-title, var(--text-title, #0f172a));

  strong {
    font-weight: 700;
  }
`
const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 4px;
  background: var(--td-step-pending, var(--general-border, #e2e8f0));
  overflow: hidden;
`
const ProgressFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: var(--td-step-current, var(--general-primary, #0027d4));
  transition: width 0.2s ease;
`
